import { buildTalentPipeline } from "./PipelineEngine";
import { computeTalentMetrics } from "./TalentMetrics";

export function buildTalentDashboard({
  users = [],
  invites = [],
  filters = {},
}) {
  const userPipelineRows = buildTalentPipeline(users);

  const progressedUserEmails = new Set(
    (users || [])
      .filter((u) => {
        const registrationStatus = String(
          u.registrationStatus || ""
        ).toLowerCase();

        const onboardingStatus = String(
          u.onboardingStatus || ""
        ).toLowerCase();

        const workerStatus = String(
          u.workerStatus || ""
        ).toLowerCase();

        const status = String(
          u.status || ""
        ).toLowerCase();

        return (
          registrationStatus === "approved" ||
          [
            "registered",
            "onboarding",
            "missing_docs",
            "in_progress",
            "submitted",
            "pending_review",
            "action_required",
            "approved",
          ].includes(onboardingStatus) ||
          [
            "onboarding",
            "under_review",
            "ready",
          ].includes(workerStatus) ||
          status === "active"
        );
      })
      .map((u) =>
        String(u.email || u.emailLower || "")
          .trim()
          .toLowerCase()
      )
      .filter(Boolean)
  );

  const invitePipelineRows = (invites || [])
    .filter((invite) => {
      const email = String(
        invite.emailLower || invite.email || ""
      )
        .trim()
        .toLowerCase();

      const invitationStatus = String(
        invite.inviteStatus ||
          invite.invitationStatus ||
          invite.status ||
          ""
      ).toLowerCase();

      const registrationStatus = String(
        invite.registrationStatus || ""
      ).toLowerCase();

      const onboardingStatus = String(
        invite.onboardingStatus || ""
      ).toLowerCase();

      if (email && progressedUserEmails.has(email)) {
        return false;
      }

      if (
        ["claimed", "registered", "approved", "cancelled", "completed"].includes(
          invitationStatus
        )
      ) {
        return false;
      }

      if (["registered", "approved"].includes(registrationStatus)) {
        return false;
      }

      if (
        [
          "registered",
          "onboarding",
          "in_progress",
          "submitted",
          "pending_review",
          "action_required",
          "approved",
        ].includes(onboardingStatus)
      ) {
        return false;
      }

      return ["invited", "sent", "pending", ""].includes(
        invitationStatus
      );
    })

.map((invite) => ({
      ...invite,

      uid:
        invite.uid ||
        invite.id ||
        invite.emailLower ||
        invite.email,

      email:
        invite.email ||
        invite.emailLower ||
        "",

      role: invite.role || "worker",

      primaryRole:
        invite.primaryRole ||
        invite.position ||
        invite.workerType ||
        "Worker",

      position:
        invite.position ||
        invite.primaryRole ||
        invite.workerType ||
        "Worker",

      source:
        invite.source ||
        invite.registrationSource ||
        "manual_invite",

      pipelineStage: "invited",
      isInvite: true,
    }));

const pipelineRows = [
  ...invitePipelineRows,
  ...userPipelineRows,
].filter(
  (u) => String(u.pipelineStage || "").toLowerCase() !== "archived"
);

const counts = computeTalentMetrics(pipelineRows, invites);

const reviewQueueWorkers = pipelineRows.filter(
  (u) => u.pipelineStage === "review_queue"
);

const approvedWorkers = pipelineRows.filter(
  (u) => u.pipelineStage === "approved"
);

  const search = String(filters.search || "")
    .trim()
    .toLowerCase();

  const roleFilter = String(filters.role || "all").toLowerCase();
  const facilityFilter = String(filters.facility || "all").toLowerCase();
  const sourceFilter = String(filters.source || "all").toLowerCase();
  const stageFilter = String(filters.stage || "all").toLowerCase();

  const filteredPipelineRows = pipelineRows
    .filter((u) => u.pipelineStage !== "approved")
    .filter((u) => {
      const role = String(
        u.position ||
          u.primaryRole ||
          u.workerType ||
          u.role ||
          u.platformRole ||
          ""
      ).toLowerCase();

      const facility = String(u.facilityId || "").toLowerCase();
      const source = String(u.source || "").toLowerCase();
      const stage = String(u.pipelineStage || "").toLowerCase();

      const name = [
        u.displayName,
        u.fullName,
        u.name,
        [u.firstName, u.lastName].filter(Boolean).join(" "),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !search ||
        String(u.email || "").toLowerCase().includes(search) ||
        name.includes(search);

      const matchesRole =
        roleFilter === "all" ||
        role === roleFilter ||
        (roleFilter === "worker" && String(u.role || "").toLowerCase() === "worker");

      const matchesFacility =
        facilityFilter === "all" ||
        facility === facilityFilter;

      const matchesSource =
        sourceFilter === "all" ||
        source === sourceFilter;

      const matchesStage =
        stageFilter === "all" ||
        stage === stageFilter;

      return (
        matchesSearch &&
        matchesRole &&
        matchesFacility &&
        matchesSource &&
        matchesStage
      );
    });

  return {
    pipelineRows,
    counts,
    reviewQueueWorkers,
    approvedWorkers,
    filteredPipelineRows,
  };
}
